import { useEntity } from '@backstage/plugin-catalog-react';
import { makeStyles } from 'tss-react/mui';
import { getColors, typography } from '@mercedes-benz/odp-theme-v2';
import Grid from '@mui/material/Grid';
import CodeIcon from '@mui/icons-material/Code';
import {
  ComplianceCard,
  ContributorsCard,
  LanguagesCard,
  ReadMeCard,
  ReleasesCard,
  isGithubInsightsAvailable,
} from '@roadiehq/backstage-plugin-github-insights';
import { BackstageGuard, GitHubGuard } from '@mercedes-benz/permission';
import { ownershipPermission } from '@mercedes-benz/permission-common';

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  return {
    emptyState: {
      background: c.background.paper,
      borderRadius: 12,
      border: `1px solid ${c.border.default}`,
      padding: 48,
      minHeight: 400,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      textAlign: 'center',
    },
    iconWrapper: {
      width: 56,
      height: 56,
      borderRadius: '50%',
      background: 'rgba(0, 120, 214, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': {
        width: 28,
        height: 28,
        color: c.primary.main,
      },
    },
    title: {
      fontFamily: typography.fontFamilyCorpoSText,
      color: c.text.primary,
      fontWeight: 600,
      fontSize: '1.1rem',
      margin: 0,
      userSelect: 'none',
    },
    description: {
      fontFamily: typography.fontFamilyCorpoSText,
      color: fgAlpha(0.55),
      fontSize: '0.875rem',
      margin: 0,
      maxWidth: 400,
      lineHeight: 1.6,
    },
    cardWrapper: {
      background: c.background.paper,
      borderRadius: 12,
      border: `1px solid ${c.border.default}`,
      overflow: 'hidden',
    },
  };
});

export const CodeInsightsTab = () => {
  const { classes } = useStyles();
  const { entity } = useEntity();

  if (!isGithubInsightsAvailable(entity)) {
    return (
      <div className={classes.emptyState}>
        <div className={classes.iconWrapper}>
          <CodeIcon />
        </div>
        <h3 className={classes.title}>No GitHub repository linked</h3>
        <p className={classes.description}>
          Add a <code>github.com/project-slug</code> annotation to enable Code
          Insights for this component.
        </p>
      </div>
    );
  }

  return (
    <GitHubGuard feature="Code Insights">
      <BackstageGuard permission={ownershipPermission}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <div className={classes.cardWrapper}>
              <ComplianceCard />
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <div className={classes.cardWrapper}>
              <LanguagesCard />
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <div className={classes.cardWrapper}>
              <ContributorsCard />
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <div className={classes.cardWrapper}>
              <ReleasesCard />
            </div>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.cardWrapper}>
              <ReadMeCard />
            </div>
          </Grid>
        </Grid>
      </BackstageGuard>
    </GitHubGuard>
  );
};
